import React, { useCallback, useEffect, useRef, useState } from 'react'
import hclogo from "../images/hclogo.svg";
import passTemplate from "../images/virtualpass.png";
import { Download } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

type RegistrationPass = {
    fullName: string;
    passId: string;
};

const toInitialCaps = (value: string) => value
    .trim()
    .toLowerCase()
    .split(/(\s+|[-'])/)
    .map((part) => {
        if (/^\s+$/.test(part) || part === "-" || part === "'") return part;
        return part ? part.charAt(0).toUpperCase() + part.slice(1) : part;
    })
    .join("");

const getSavedRegistration = (): RegistrationPass | null => {
    try {
        const savedRegistration = sessionStorage.getItem("haystack_registration");
        const registration = savedRegistration ? JSON.parse(savedRegistration) : null;
        return registration
            ? { ...registration, fullName: toInitialCaps(registration.fullName) }
            : null;
    } catch {
        return null;
    }
};

const SuccessPage = () => {
    const location = useLocation();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [passImage, setPassImage] = useState<HTMLImageElement | null>(null);
    const [registration] = useState<RegistrationPass | null>(() =>
        location.state
            ? {
                ...(location.state as RegistrationPass),
                fullName: toInitialCaps((location.state as RegistrationPass).fullName),
            }
            : getSavedRegistration()
    );

    const drawPass = useCallback((image: HTMLImageElement) => {
        const canvas = canvasRef.current;
        if (!canvas || !registration) return;

        const context = canvas.getContext("2d");
        if (!context) return;

        canvas.width = image.width;
        canvas.height = image.height;
        context.drawImage(image, 0, 0);
        context.fillStyle = "#3d567a";
        context.textBaseline = "top";

        const nameX = canvas.width * 0.32;
        const nameY = canvas.height * 0.31;
        const maxNameWidth = canvas.width * 0.5;
        let nameFontSize = canvas.width * 0.055;
        context.font = `bold ${nameFontSize}px Manrope, sans-serif`;

        while (
            context.measureText(registration.fullName).width > maxNameWidth &&
            nameFontSize > 10
        ) {
            nameFontSize -= 1;
            context.font = `bold ${nameFontSize}px Manrope, sans-serif`;
        }
        context.fillText(registration.fullName, nameX, nameY);

        const idX = canvas.width * 0.32;
        const idY = canvas.height * 0.45;
        const maxIdWidth = canvas.width * 0.5;
        let idFontSize = canvas.width * 0.065;
        context.font = `bold ${idFontSize}px "Mozilla Headline", sans-serif`;

        while (
            context.measureText(registration.passId).width > maxIdWidth &&
            idFontSize > 10
        ) {
            idFontSize -= 1;
            context.font = `bold ${idFontSize}px "Mozilla Headline", sans-serif`;
        }
        context.fillText(registration.passId, idX, idY);
    }, [registration]);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Registration Successful | Haystack Conference";

        if (!registration) {
            setIsLoading(false);
            return;
        }

        let isCancelled = false;
        const image = new Image();
        image.onload = async () => {
            if (document.fonts) {
                await Promise.all([
                    document.fonts.load("700 48px Manrope"),
                    document.fonts.load('700 48px "Mozilla Headline"'),
                ]).catch(() => {
                    // The canvas can still render with the declared fallback fonts.
                });
            }
            if (!isCancelled) {
                setPassImage(image);
                setIsLoading(false);
            }
        };
        image.onerror = () => {
            if (!isCancelled) setIsLoading(false);
        };
        image.src = passTemplate;

        return () => {
            isCancelled = true;
        };
    }, [registration]);

    useEffect(() => {
        if (passImage) drawPass(passImage);
    }, [drawPass, passImage]);

    const handleDownload = () => {
        const canvas = canvasRef.current;
        if (!canvas || !registration) return;

        setIsDownloading(true);
        canvas.toBlob((blob) => {
            if (!blob) {
                setIsDownloading(false);
                return;
            }

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `haystack-pass-${registration.passId}.png`;
            link.click();
            URL.revokeObjectURL(url);
            setIsDownloading(false);
        }, "image/png", 1);
    };

    if (!registration) {
        return (
            <main className="flex min-h-screen items-center justify-center bg-[#161515] px-6 py-12 text-center text-white">
                <section className="w-full max-w-xl rounded-3xl border border-white/10 bg-white/5 px-6 py-12 shadow-2xl sm:px-10">
                    <img src={hclogo} alt="Haystack Conference" className="mx-auto h-10 sm:h-12" />
                    <h1 className="mt-9 font-clashSemibold text-3xl">Pass details unavailable</h1>
                    <p className="mx-auto mt-4 max-w-md font-clashRegular leading-7 text-white/65">
                        Please complete the registration form to generate your virtual pass.
                    </p>
                    <Link to="/register" className="mt-8 inline-flex rounded-xl bg-greenY px-7 py-3 font-clashMedium text-deepGreen">
                        Go to registration
                    </Link>
                </section>
            </main>
        );
    }

    return (
        <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#161515] px-5 py-10 sm:px-8 sm:py-14">
            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-greenY/10 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-[#3d567a]/20 blur-3xl" aria-hidden="true" />

            <section className="relative w-full max-w-4xl rounded-3xl border border-white/10 bg-white/[0.04] px-5 py-8 shadow-2xl backdrop-blur-sm sm:px-10 sm:py-12">
                <img src={hclogo} alt="Haystack Conference" className="h-9 sm:h-12" />

                <div className="mx-auto mt-9 flex max-w-2xl flex-col items-center text-center">
                    <p className="font-clashSemibold text-xs uppercase tracking-[0.25em] text-greenY">
                        Registration complete
                    </p>
                    <h1 className="mt-3 font-clashSemibold text-3xl text-white sm:text-4xl">
                        You&rsquo;re all set, {registration.fullName.split(" ")[0]}!
                    </h1>
                    <p className="mt-3 font-clashRegular text-base leading-7 text-white/65 sm:text-lg">
                        Your Haystack Conference virtual pass is ready. Save it to your device and bring it with you.
                    </p>

                    <div className="mt-8 w-full max-w-md overflow-hidden rounded-2xl bg-white/5 p-2 shadow-2xl ring-1 ring-white/10 sm:p-3">
                        {isLoading && (
                            <div className="flex aspect-square items-center justify-center rounded-xl bg-white/5 font-clashRegular text-white/60">
                                Preparing your pass&hellip;
                            </div>
                        )}
                        <canvas
                            ref={canvasRef}
                            aria-label={`Virtual pass for ${registration.fullName}, ID ${registration.passId}`}
                            className={`${isLoading ? "hidden" : "block"} h-auto w-full rounded-xl`}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={isLoading || isDownloading}
                        className="mt-8 inline-flex min-w-[210px] items-center justify-center gap-2 rounded-xl bg-greenY px-7 py-3.5 font-clashMedium text-deepGreen shadow-lg transition hover:-translate-y-0.5 hover:bg-[#f8d58b] disabled:cursor-wait disabled:opacity-60"
                    >
                        <Download size={19} aria-hidden="true" />
                        {isDownloading ? "Downloading…" : "Download virtual pass"}
                    </button>
                </div>
            </section>
        </main>
    )
}

export default SuccessPage
